<template>
  <div class="masters-registration-panel t-text" data-qe-id="registration-panel">
    <div class="registration-info">
      <div class="registration-info-header-wrapper">
        <div class="registration-info-header">
          <div></div>
        </div>
      </div>
      <h1 data-qe-id="tournament-name">{{ tournamentUi.name }}</h1>
      <c-tournament-info
        :tournament-ui="tournamentUi"
        class="tournament-info-box"
        gameName
        totalWin
        addedPrize
        entryFee
        restartFee
        competitionDateLobbyTime
        registrationDate
      />
      <c-tech-hint
        class="c-tech-button"
        v-if="!registrationIsOver"
        :gametype="tournamentUi.gametype"
        :techs="tournamentUi.metaData.gameTechs"
      >
        <template v-slot:default="status">
          <t-tooltip
            :disabled="registrationPossible || status.block"
            :text="
              $t('tViews.registrationInfo.registrationDidNotStart_info', {
                registrationStart,
              })
            "
          >
            <t-button
              yellow
              l
              :inactive="status.block"
              :disabled="!(registrationPossible || status.block)"
              @click="register(status.block)"
              data-qe-id="register-button"
            >
              {{ $t('tViews.registrationInfo.button') }}
            </t-button>
          </t-tooltip>
        </template>
      </c-tech-hint>
      <p v-else class="t-inlay-tip t-tc">
        {{ $t('tViews.registrationInfo.registrationTimeOver') }}
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { DateFormat } from '@gd/i18n'
import CTournamentInfo from '@/components/CTournamentInfo.vue'
import { TournamentStatus } from '@/model/TournamentStatus'
import { TournamentUi } from '@/model/Tournament'

const props = defineProps<{ tournamentUi: TournamentUi }>()
const emit = defineEmits(['register'])
const { d } = useI18n()

function register(blocked: boolean) {
  if (blocked) {
    return
  }
  if (registrationPossible.value) emit('register')
}

const registrationStart = computed(() => {
  return d(new Date(props.tournamentUi.dates.registrationStart), DateFormat.DDMMYYY)
})

const registrationPossible = computed(() => {
  return props.tournamentUi.isRegistrationPossible
})
/**
 * the case that the registration time ended and a tournament is not
 * in running mode is not handled ( because it should not happen )
 */
const registrationIsOver = computed(() => {
  return !(registrationPossible.value || props.tournamentUi.tournamentStatus === TournamentStatus.UPCOMING)
})
</script>

<style lang="less">
@import '~@npm/@gd/common-styles/lib/color-vars';
@import '../../styles/vars';
.masters-registration-panel {
  position: relative;
  background-image: linear-gradient(to right, #6d4b00 0%, #f0eb9d 30%, #f0eb9d 70%, #6d4b00 100%);
  padding: 3px;
  border-radius: 0.4em;
  margin-bottom: 6.25em;
  box-shadow: 0 0.5em 2em rgba(0, 0, 0, 0.3);

  .registration-info-header-wrapper {
    overflow: hidden;
    border-radius: 0.1875em 0.1875em 0 0;
    margin-bottom: 1.5em;
  }
  .registration-info-header {
    content: '';
    height: 4.75em;
    background: url('../../assets/skatmasters/skat_teaser_header_center.png') center bottom no-repeat;
    width: 34.625em;
    margin: 0 auto;
    position: relative;
    &:before,
    &:after {
      content: '';
      height: 4.75em;
      position: absolute;
      width: 100%;
      top: 0;
    }
    &:before {
      background: url('../../assets/skatmasters/skat_teaser_header_bg_left_right.png') center bottom repeat-x;
      left: -100%;
    }
    &:after {
      background: url('../../assets/skatmasters/skat_teaser_header_bg_left_right.png') center bottom repeat-x;
      right: -100%;
    }
  }
  .registration-info {
    background: url('../../assets/skatmasters/skat_rays.png') center bottom no-repeat,
      url('../../assets/skatmasters/skat_bg.jpg');
    position: relative;
    border-radius: 0.1875em;
    &:after {
      content: '';
      top: 0;
      left: 0;
      position: absolute;
      width: 100%;
      height: 100%;
      box-shadow: 0 0 0.8em rgba(0, 0, 0, 0.4) inset;
      border-radius: 0.1875em;
      pointer-events: none;
    }

    h1 {
      margin-top: 0;
      text-shadow: 0 0.2rem 0 rgba(16, 49, 8, 0.4);
      color: @c1-a;
      text-transform: uppercase;
    }
    h3 {
      text-shadow: 0 0.2rem 0 rgba(16, 49, 8, 0.4);
      color: @c1-a;
    }
    .tournament-info-box {
      color: @c1-a;
      margin: 0 auto -1em auto;
      font-size: 1.25em;
      line-height: 1.25em;
      max-width: 40em;
      text-shadow: 0 0.2rem 0 rgba(16, 49, 8, 0.4);
      position: relative;
      z-index: 1;
      padding: 0 @smallerspacing;
      .layout {
        background: none;
        padding-top: 0;
      }
    }
    .registration-time-over {
      color: red;
    }
    .c-tech-button {
      position: relative;
      bottom: -1.9em;
      z-index: 1;
    }
    .t-inlay-tip {
      display: inline-block;
      margin: 1.5em 1em;
    }
  }
  .icon-podium {
    fill: @c1-a;
    filter: drop-shadow(0 0.2rem 0 rgba(16, 49, 8, 0.4));
  }
  .info-icon {
    color: @c1-a;
  }
  .tournament-info {
    .name {
      flex: 50 0;
      text-align: right;
    }
    .value {
      flex: 50 0;
    }
  }
}
</style>
